import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCloudArrowUp,
} from '@fortawesome/free-solid-svg-icons';
import { Footer } from '../components/Footer';
import { HomeMobileLanding } from '../components/home/HomeMobileLanding';
import {
  FinalCtaSection,
  HowItWorksSection,
  SamplesSection,
} from '../components/home/homeSections';
import { ScoreUploadBox } from '../components/ScoreUploadBox';
import { MobileAppWaitlistBanner } from '../components/MobileAppWaitlistBanner';
import { usePageMeta } from '../hooks/usePageMeta';
import { getPublicPageMeta } from '../seo/publicPageMeta';
import { useIsPhone } from '../hooks/useIsPhone';
import { useMobileOS } from '../hooks/useMobileOS';
import { useIsAuthenticated } from 'src/auth/firebaseMsalCompat';
import barBeforeImg from '@Assets/images/home/bar_before.png';
import barAfterImg from '@Assets/images/home/bar_after.png';
import {
  reactPlugin,
  trackButtonClick,
  trackPageView,
} from '../services/telemetry';
import {
  useAppInsightsContext,
  withAITracking,
} from '@microsoft/applicationinsights-react-js';

function Home() {
  const isAuthenticated = useIsAuthenticated();
  const appInsights = useAppInsightsContext();
  const navigate = useNavigate();
  // The waitlist banner now targets iOS only; Android visitors get the
  // "Open in Google Play" banner (rendered globally above the NavBar) instead.
  const mobileOS = useMobileOS();
  const { isPhone } = useIsPhone();
  const desktopFromMd = isPhone ? '' : 'md:block';
  const desktopFlexFromMd = isPhone ? '' : 'md:flex';

  // The home page is a guest/marketing surface — its CTAs and the upload box
  // only apply to logged-out / guest visitors. Real signed-in users are sent
  // to their workspace. `useIsAuthenticated()` is false for anonymous guests,
  // so guests (and logged-out visitors) stay here.
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/workspace', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // SEO: per-page meta tags, canonical, OG & Twitter
  usePageMeta(getPublicPageMeta('/'));

  useEffect(() => {
    trackPageView('Home', '/');
    appInsights.trackPageView({
      name: 'Home Page',
      isLoggedIn: isAuthenticated,
    });
  }, [isAuthenticated, appInsights]);

  // The bottom CTA scrolls back up to the guest upload box at the top of the page.
  const handleUploadCtaClick = () => {
    trackButtonClick('Home', 'Upload My Score');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Avoid flashing the marketing/guest page while the workspace redirect runs.
  if (isAuthenticated) {
    return null;
  }

  return (
    <>
      {mobileOS === 'ios' && <MobileAppWaitlistBanner source="Home" />}

      <main
        className={`
          bg-gray-25 flex min-h-screen flex-col items-center p-4 font-[Outfit]
        `}
      >
        <div className={isPhone ? undefined : 'md:hidden'}>
          <HomeMobileLanding />
        </div>

        <header
          className={`
            mb-8 hidden text-center sm:mb-12 lg:mb-16
            ${desktopFromMd}
          `}
        >
          <h1
            className={`
              mt-2 mb-1 font-bold text-gray-800
              sm:mt-3 sm:mb-2 sm:text-3xl
              md:mt-4 md:mb-3 md:text-4xl
              lg:mt-6 lg:mb-4 lg:text-5xl
            `}
          >
            Upload your sheet music.
            <br />
            <span className="text-yellow-700">Get every fingering.</span>
          </h1>
          <p
            className={`
              mx-auto max-w-2xl text-[14px] text-gray-700
              sm:text-xl
              md:text-2xl
              lg:text-2xl
            `}
          >
            Any piano score — PDF, MusicXML, or image. AI marks the fingerings.
          </p>
        </header>

        {/* Guest upload: lets logged-out visitors generate fingering once
            without an account (anonymous Firebase user, demo limits). Only
            guests reach this page — signed-in users are redirected to the
            workspace above. Flanked by a before/after example (plain score ->
            AI fingering + alt fingering + accidental highlighting) with arrows
            illustrating the flow. */}
        <div
          className={`
            mb-8 hidden w-full max-w-6xl rounded-3xl bg-gradient-to-b
            from-yellow-50 to-white p-6 shadow-sm
            sm:p-10
            ${desktopFromMd}
          `}
        >
          {/* Boxed upload for desktop/tablet. Phones use HomeMobileLanding. */}
          <div
            className={`
              hidden items-center justify-center gap-3
              lg:gap-6
              ${desktopFlexFromMd}
            `}
          >
            <div className="shrink-0 text-center">
              <span
                className={`
                  mb-1.5 block text-[10px] font-bold tracking-wider
                  text-orange-600 uppercase
                `}
              >
                Bare score
              </span>
              <img
                src={barBeforeImg}
                alt="A bar of piano sheet music before AI fingering"
                className={`
                  w-full max-w-[180px] rounded-lg bg-white p-2 shadow-sm
                  lg:max-w-[260px]
                `}
              />
            </div>

            <FontAwesomeIcon
              icon={faArrowRight}
              aria-hidden="true"
              className="shrink-0 text-xl text-yellow-500 lg:text-2xl"
            />

            <div className="w-full max-w-xs shrink-0 lg:max-w-sm">
              <ScoreUploadBox variant="guest" source="Home" layout="boxed" />
            </div>

            <FontAwesomeIcon
              icon={faArrowRight}
              aria-hidden="true"
              className="shrink-0 text-xl text-yellow-500 lg:text-2xl"
            />

            <div className="shrink-0 text-center">
              <span
                className={`
                  mb-1.5 block text-[10px] font-bold tracking-wider
                  text-emerald-800 uppercase
                `}
              >
                Fingered
              </span>
              <img
                src={barAfterImg}
                alt="The same bar after AI processing, showing suggested fingerings, alternative fingerings, and highlighted accidentals"
                className={`
                  w-full max-w-[180px] rounded-lg bg-white p-2 shadow-sm
                  lg:max-w-[260px]
                `}
              />
            </div>
          </div>
        </div>

        <SamplesSection
          surface="desktop"
          className={`
            mt-2 hidden w-full px-3
            md:max-w-3xl
            lg:max-w-4xl
            ${desktopFromMd}
          `}
        />

        <HowItWorksSection
          className={`
            mt-10 hidden w-full px-3
            md:max-w-3xl
            lg:mt-14 lg:max-w-4xl
            ${desktopFromMd}
          `}
        />

        <div
          className={`
            mt-10 hidden w-full px-3 text-center
            md:max-w-3xl
            lg:mt-14 lg:max-w-4xl
            ${desktopFromMd}
          `}
        >
          <h2
            className={`
              mb-5 text-xl font-bold text-gray-800
              md:mb-7 md:text-3xl
              lg:mb-9 lg:text-4xl
            `}
          >
            Why pianists use it
          </h2>
          <ul
            className={`
              mx-auto space-y-3 text-left text-base text-gray-500
              md:space-y-4 md:text-lg
              lg:space-y-5 lg:text-xl
            `}
          >
            <li className="flex items-start gap-3">
              <span className="mt-1 text-yellow-500">✓</span>
              <span>
                <strong className="text-gray-700">
                  Save hours of practice time
                </strong>
                —start with AI-suggested fingerings instead of figuring them out
                from scratch.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 text-yellow-500">✓</span>
              <span>
                <strong className="text-gray-700">
                  Discover new possibilities
                </strong>
                —our AI is trained on data labeled by professional pianists,
                offering fresh perspectives on piano fingering techniques.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 text-yellow-500">✓</span>
              <span>
                <strong className="text-gray-700">Fully customizable</strong>
                —choose between alternative fingerings and edit any fingering to
                match your hand size and playing style.
              </span>
            </li>
          </ul>
        </div>

        <div
          className={`
            mt-10 hidden h-px w-full max-w-5xl bg-gray-300
            lg:mt-14
            ${desktopFromMd}
          `}
        ></div>

        <FinalCtaSection
          className={`
            mt-8 mb-10 hidden w-full
            md:max-w-3xl
            lg:mt-12 lg:mb-14 lg:max-w-4xl
            ${desktopFromMd}
          `}
          label="Upload my score"
          icon={faCloudArrowUp}
          onClick={handleUploadCtaClick}
        />
      </main>

      <Footer />
    </>
  );
}

const HomeWithAITracking = withAITracking(reactPlugin, Home);

export { HomeWithAITracking as Home };
